import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { mapSettingsToOrganizationSettings, defaultSettings } from '@/utils/mapOrganizationSettings';

export interface OrganizationSettings {
  site_title: string | null;
  site_description: string | null;
  favicon_url: string | null;
  primary_color: string;
  secondary_color: string;
  background_color: string;
  foreground_color: string;
  font_heading: string;
  font_body: string;
  enable_sandbox: boolean;
  enable_library: boolean;
  enable_events: boolean;
  enable_ai_chat: boolean;
  enable_integrations: boolean;
  require_auth: boolean;
  library_requires_auth: boolean;
  planner_requires_auth: boolean;
  contact_email: string | null;
  planner_label: string | null;
  planner_system_prompt: string | null;
  planner_mode: 'chat' | 'wizard';
  planner_welcome_subheader: string | null;
  planner_welcome_example: string | null;
  planner_wizard_config: Record<string, unknown> | null;
  ai_system_instructions: string | null;
  ai_context_provider: 'none' | 'lovable' | 'magisterium';
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  is_flagship: boolean;
  primary_domain: string | null;
  settings: OrganizationSettings;
}

export type SimulatedAuthState = 'authenticated' | 'unauthenticated' | null;

interface OrganizationContextType {
  organization: Organization | null;
  isLoading: boolean;
  error: Error | null;
  isSuperAdmin: boolean;
  isOwner: boolean;
  refetch: () => Promise<void>;
  // Impersonation
  impersonatedOrgId: string | null;
  impersonatedRole: string | null;
  impersonatedOrgName: string | null;
  simulatedAuthState: SimulatedAuthState;
  setImpersonation: (orgId: string, role: string, authState?: SimulatedAuthState) => void;
  clearImpersonation: () => void;
}

export { defaultSettings };

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// Helper to get/set impersonation from sessionStorage
const getStoredImpersonation = () => {
  try {
    const stored = sessionStorage.getItem('impersonation');
    if (stored) {
      return JSON.parse(stored) as { 
        orgId: string; 
        role: string; 
        orgName: string;
        authState?: SimulatedAuthState;
      };
    }
  } catch (e) {
    console.error('Error reading impersonation from sessionStorage:', e);
  }
  return null;
};

const setStoredImpersonation = (orgId: string, role: string, orgName: string, authState: SimulatedAuthState) => {
  try {
    sessionStorage.setItem('impersonation', JSON.stringify({ orgId, role, orgName, authState }));
  } catch (e) {
    console.error('Error storing impersonation:', e);
  }
};

const clearStoredImpersonation = () => {
  try {
    sessionStorage.removeItem('impersonation');
  } catch (e) {
    console.error('Error clearing impersonation:', e);
  }
};

// ============= Organization Cache Helpers =============
const ORG_CACHE_PREFIX = 'org_cache_';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedOrganization {
  organization: Organization;
  timestamp: number;
  version: string;
}

// Default flagship organization for instant render (no loading screen)
const defaultFlagshipOrg: Organization = {
  id: 'default-flagship',
  name: 'Build With Christ',
  slug: 'buildwithchrist',
  logo_url: null,
  is_flagship: true,
  primary_domain: null,
  settings: defaultSettings,
};

// Synchronous cache check for instant initial render
const getInitialOrganization = (): Organization => {
  try {
    const hostname = window.location.hostname;
    const cached = localStorage.getItem(`${ORG_CACHE_PREFIX}${hostname}`);
    if (cached) {
      const parsed: CachedOrganization = JSON.parse(cached);
      // Basic validity check (within 24 hours) - skip version check for instant render
      const isNotExpired = Date.now() - parsed.timestamp < CACHE_EXPIRY_MS;
      if (isNotExpired) {
        return parsed.organization;
      }
    }
  } catch (e) {
    // Ignore errors, return default
  }
  return defaultFlagshipOrg;
};

const getCachedOrganization = (hostname: string, currentVersion: string): Organization | null => {
  try {
    const cached = localStorage.getItem(`${ORG_CACHE_PREFIX}${hostname}`);
    if (cached) {
      const parsed: CachedOrganization = JSON.parse(cached);
      // Check if cache is still valid (within 24 hours AND version matches)
      const isNotExpired = Date.now() - parsed.timestamp < CACHE_EXPIRY_MS;
      const isVersionValid = parsed.version === currentVersion;
      
      if (isNotExpired && isVersionValid) {
        return parsed.organization;
      }
      // Expired or version mismatch, remove it
      localStorage.removeItem(`${ORG_CACHE_PREFIX}${hostname}`);
    }
  } catch (e) {
    console.error('Error reading org cache:', e);
  }
  return null;
};

const setCachedOrganization = (hostname: string, org: Organization, version: string): void => {
  try {
    const cacheData: CachedOrganization = {
      organization: org,
      timestamp: Date.now(),
      version,
    };
    localStorage.setItem(`${ORG_CACHE_PREFIX}${hostname}`, JSON.stringify(cacheData));
  } catch (e) {
    console.error('Error setting org cache:', e);
  }
};

const fetchCacheVersion = async (): Promise<string> => {
  try {
    const { data } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'CACHE_VERSION')
      .is('organization_id', null)
      .maybeSingle();
    return data?.value || '1';
  } catch (e) {
    console.error('Error fetching cache version:', e);
    return '1';
  }
};

export function OrganizationProvider({ children }: { children: ReactNode }) {
  // Initialize with cached org or default flagship for instant render - no loading screen!
  const [organization, setOrganization] = useState<Organization | null>(getInitialOrganization);
  const [isLoading, setIsLoading] = useState(false); // Start as false for instant render
  const [error, setError] = useState<Error | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  
  // Impersonation state
  const [impersonatedOrgId, setImpersonatedOrgId] = useState<string | null>(null);
  const [impersonatedRole, setImpersonatedRole] = useState<string | null>(null);
  const [impersonatedOrgName, setImpersonatedOrgName] = useState<string | null>(null);
  const [simulatedAuthState, setSimulatedAuthState] = useState<SimulatedAuthState>(null);

  // Initialize impersonation state from sessionStorage
  useEffect(() => {
    const stored = getStoredImpersonation();
    if (stored) {
      setImpersonatedOrgId(stored.orgId);
      setImpersonatedRole(stored.role);
      setImpersonatedOrgName(stored.orgName);
      setSimulatedAuthState(stored.authState || null);
    }
  }, []);

  const setImpersonation = useCallback(async (orgId: string, role: string, authState: SimulatedAuthState = 'authenticated') => {
    // Fetch the org name for display
    const { data: orgData } = await supabase
      .from('organizations')
      .select('name')
      .eq('id', orgId)
      .single();
    
    const orgName = orgData?.name || 'Unknown Organization';
    
    setImpersonatedOrgId(orgId);
    setImpersonatedRole(role);
    setImpersonatedOrgName(orgName);
    setSimulatedAuthState(authState);
    setStoredImpersonation(orgId, role, orgName, authState);
    
    // Refetch organization data for the impersonated org
    await fetchOrgById(orgId);
  }, []);

  const clearImpersonation = useCallback(() => {
    setImpersonatedOrgId(null);
    setImpersonatedRole(null);
    setImpersonatedOrgName(null);
    setSimulatedAuthState(null);
    clearStoredImpersonation();
    
    // Refetch original organization
    resolveOrganization();
  }, []);

  const fetchOrgById = async (orgId: string) => {
    try {
      setIsLoading(true);
      
      // Fetch organization details
      const { data: orgDetails } = await supabase
        .from('organizations')
        .select('id, name, slug, logo_url, is_flagship')
        .eq('id', orgId)
        .single();

      // Fetch org settings using secure RPC function (excludes sensitive fields)
      const { data: settingsArray } = await supabase
        .rpc('get_public_organization_settings', { org_id: orgId });
      const settingsData = settingsArray?.[0] ?? null;

      // Fetch primary domain
      const { data: domainData } = await supabase
        .from('organization_domains')
        .select('domain')
        .eq('organization_id', orgId)
        .eq('is_primary', true)
        .maybeSingle();

      const settings = mapSettingsToOrganizationSettings(settingsData, defaultSettings);

      if (orgDetails) {
        setOrganization({
          id: orgDetails.id,
          name: orgDetails.name,
          slug: orgDetails.slug,
          logo_url: orgDetails.logo_url ?? null,
          is_flagship: orgDetails.is_flagship ?? false,
          primary_domain: domainData?.domain ?? null,
          settings,
        });
      }
    } catch (err) {
      console.error('Error fetching organization by ID:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const resolveOrganization = async (silent: boolean = false) => {
    try {
      if (!silent) {
        setIsLoading(true);
      }
      const hostname = window.location.hostname;
      
      // Check for ?org= query parameter first (for preview testing)
      const urlParams = new URLSearchParams(window.location.search);
      const orgParam = urlParams.get('org');

      let orgId: string | null = null;
      let orgName = 'Build With Christ';
      let orgSlug = 'buildwithchrist';

      // If ?org= parameter exists, resolve by slug
      if (orgParam) {
        const { data: orgBySlug } = await supabase
          .from('organizations')
          .select('id, name, slug')
          .eq('slug', orgParam)
          .eq('is_active', true)
          .maybeSingle();

        if (orgBySlug) {
          orgId = orgBySlug.id;
          orgName = orgBySlug.name;
          orgSlug = orgBySlug.slug;
        }
      }

      // If no org from param, try to resolve by domain
      if (!orgId) {
        const { data: orgData, error: orgError } = await supabase
          .rpc('resolve_organization_by_domain', { _domain: hostname });

        if (orgError) {
          console.error('Error resolving organization:', orgError);
        }

        if (orgData && orgData.length > 0) {
          orgId = orgData[0].organization_id;
          orgName = orgData[0].organization_name;
          orgSlug = orgData[0].organization_slug;
        }
      }

      // Fallback to flagship org for development/preview URLs
      if (!orgId) {
        const { data: flagshipOrg } = await supabase
          .from('organizations')
          .select('id, name, slug, logo_url, is_flagship')
          .eq('is_flagship', true)
          .eq('is_active', true)
          .maybeSingle();

        if (flagshipOrg) {
          orgId = flagshipOrg.id;
          orgName = flagshipOrg.name;
          orgSlug = flagshipOrg.slug;
        }
      }

      if (orgId) {
        // Fetch organization details
        const { data: orgDetails } = await supabase
          .from('organizations')
          .select('id, name, slug, logo_url, is_flagship')
          .eq('id', orgId)
          .single();

        // Fetch org settings using secure RPC function (excludes sensitive fields)
        const { data: settingsArray } = await supabase
          .rpc('get_public_organization_settings', { org_id: orgId });
        const settingsData = settingsArray?.[0] ?? null;

        // Fetch primary domain
        const { data: domainData } = await supabase
          .from('organization_domains')
          .select('domain')
          .eq('organization_id', orgId)
          .eq('is_primary', true)
          .maybeSingle();

        const settings = mapSettingsToOrganizationSettings(settingsData, defaultSettings);

        const resolvedOrg: Organization = {
          id: orgId,
          name: orgName,
          slug: orgSlug,
          logo_url: orgDetails?.logo_url ?? null,
          is_flagship: orgDetails?.is_flagship ?? false,
          primary_domain: domainData?.domain ?? null,
          settings,
        };

        setOrganization(resolvedOrg);

        // Cache the resolved organization for future visits
        const version = await fetchCacheVersion();
        setCachedOrganization(hostname, resolvedOrg, version);
      }

      // Check if current user is super admin or owner
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .in('role', ['super_admin', 'owner']);

        const roles = roleData?.map(r => r.role) ?? [];
        setIsSuperAdmin(roles.includes('super_admin') || roles.includes('owner'));
        setIsOwner(roles.includes('owner'));
      }
    } catch (err) {
      console.error('Error in organization resolution:', err);
      setError(err as Error);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        // Check if we have impersonation stored
        const stored = getStoredImpersonation();
        
        if (stored) {
          // Impersonation mode - use fetchOrgById (now handles loading state)
          await fetchOrgById(stored.orgId);
          
          // Check super admin and owner status
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { data: roleData } = await supabase
              .from('user_roles')
              .select('role')
              .eq('user_id', user.id)
              .in('role', ['super_admin', 'owner']);
            
            const roles = roleData?.map(r => r.role) ?? [];
            setIsSuperAdmin(roles.includes('super_admin') || roles.includes('owner'));
            setIsOwner(roles.includes('owner'));
          }
        } else {
          // Normal mode - use cache-first strategy
          const hostname = window.location.hostname;
          const currentVersion = await fetchCacheVersion();
          const cached = getCachedOrganization(hostname, currentVersion);
          
          if (cached) {
            // Use cached data immediately
            setOrganization(cached);
            
            // Check super admin and owner status BEFORE setting loading to false
            // This ensures AdminLayout renders with correct isSuperAdmin value
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              const { data: roleData } = await supabase
                .from('user_roles')
                .select('role')
                .eq('user_id', user.id)
                .in('role', ['super_admin', 'owner']);
              
              const roles = roleData?.map(r => r.role) ?? [];
              setIsSuperAdmin(roles.includes('super_admin') || roles.includes('owner'));
              setIsOwner(roles.includes('owner'));
            }
            
            // NOW set loading to false - both org and super admin status are ready
            setIsLoading(false);
            
            // Revalidate organization in background (silent mode)
            resolveOrganization(true);
          } else {
            // No cache or version mismatch - normal resolve with loading screen
            await resolveOrganization();
          }
        }
      } catch (err) {
        console.error('Error initializing organization:', err);
      } finally {
        // Ensure loading is always set to false
        setIsLoading(false);
      }
    };
    
    init();
  }, []);

  // Keep super admin status in sync with auth changes (e.g. right after login)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        // Defer Supabase calls to avoid deadlocks inside the callback
        setTimeout(() => {
          (async () => {
            try {
              const { data, error } = await supabase
                .from('user_roles')
                .select('role')
                .eq('user_id', session.user.id)
                .in('role', ['super_admin', 'owner']);

              if (error) {
                console.error('Error checking super admin role on auth change:', error);
              }

              const roles = data?.map(r => r.role) ?? [];
              setIsSuperAdmin(roles.includes('super_admin') || roles.includes('owner'));
              setIsOwner(roles.includes('owner'));
            } catch (err) {
              console.error('Unexpected error checking super admin role on auth change:', err);
            }
          })();
        }, 0);
      } else {
        setIsSuperAdmin(false);
        setIsOwner(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <OrganizationContext.Provider value={{ 
      organization, 
      isLoading, 
      error, 
      isSuperAdmin,
      isOwner,
      refetch: resolveOrganization,
      impersonatedOrgId,
      impersonatedRole,
      impersonatedOrgName,
      simulatedAuthState,
      setImpersonation,
      clearImpersonation,
    }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
